import { fieldContentName } from '../form-config/mark-zones.js';
const NUMERIC_TYPES = new Set(['number', 'decimal', 'currency']);
function err(code, message, formId) {
    return { level: 'error', code, message, ...(formId ? { formId } : {}) };
}
function checkAmountField(form, fieldName, out) {
    // amountField / appliedAmountField store the field's internalName (see
    // RemittanceInstrument's type contract in form-config/content.ts) — the
    // same identity the SP2 runtime amount reader and transaction payload
    // resolve by. Match via fieldContentName (internalName ?? name), never
    // bare `.name`: a display name diverging from internalName would
    // otherwise false-positive amount_field_not_found at publish.
    const field = (form.fields ?? []).find((f) => fieldContentName(f) === fieldName);
    if (!field) {
        out.push(err('amount_field_not_found', `Form "${form.name}": amount field "${fieldName}" does not exist on the form.`, form.id));
        return;
    }
    const dataType = field.dataType ?? 'text';
    if (!NUMERIC_TYPES.has(dataType)) {
        out.push(err('amount_field_not_numeric', `Form "${form.name}": amount field "${fieldName}" must be numeric (number/decimal/currency), got "${dataType}".`, form.id));
    }
}
export function validateRemittanceConfig(v3, surface) {
    const out = [];
    const forms = v3.forms ?? [];
    const anyKeys = v3.remittance !== undefined ||
        forms.some((f) => f.remittanceRole !== undefined || f.instrument !== undefined || f.appliedAmountField !== undefined);
    // SP4a — transaction-shape destinations are only valid on remittance jobs.
    // Runs on BOTH surfaces (a transactions-shape dest on a standard job is an error).
    for (const dest of v3.exportDestinations ?? []) {
        if (dest.payloadShape === 'transactions' && surface !== 'remittance') {
            out.push(err('remittance_transaction_shape_requires_remittance_surface', `Export destination "${dest.name}" uses payloadShape "transactions", which requires a remittance-surface job type.`));
        }
    }
    if (surface !== 'remittance') {
        if (anyKeys) {
            out.push(err('remittance_key_on_standard_surface', 'Remittance configuration keys are only valid on remittance-surface job types.'));
        }
        return out;
    }
    // SP4a (refines SP3) — remittance jobs stay ACCUMULATE-ONLY (accept advances docs
    // via updateMany, which never fires the streaming per-doc export enqueue; no sweep
    // re-fires a streaming pending@export doc → silent stranding). Transaction-shape
    // additionally requires grouping:'batch' (one payment file per batch — BAI2/lockbox).
    for (const dest of v3.exportDestinations ?? []) {
        const mode = dest.triggerPolicy?.mode;
        const grouping = dest.triggerPolicy?.grouping;
        if (mode !== 'accumulate') {
            out.push(err('remittance_streaming_export_unsupported', `Export destination "${dest.name}" cannot use a streaming export destination on a remittance job type; use an accumulate/batch destination.`));
        }
        if (dest.payloadShape === 'transactions' && !(mode === 'accumulate' && grouping === 'batch')) {
            out.push(err('remittance_transaction_shape_requires_accumulate_batch', `Export destination "${dest.name}" uses payloadShape "transactions", which requires an accumulate trigger policy with grouping "batch" (one payment file per batch).`));
        }
    }
    // Remittance surface — config is only REQUIRED once any remittance key is in
    // play; a brand-new remittance job with zero keys publishes clean until the
    // remittance sections are configured. But a job with roles and no job config
    // (or vice versa) is incomplete:
    if (!anyKeys)
        return out;
    if (!v3.remittance) {
        out.push(err('missing_remittance_job_config', 'Remittance job config (groupingMode) is required once remittance roles are declared.'));
    }
    for (const form of forms) {
        const role = form.remittanceRole;
        if (!role) {
            out.push(err('form_missing_remittance_role', `Form "${form.name}" has no remittance role — every form on a remittance job must declare coupon/payment/attachment (never defaulted).`, form.id));
            continue;
        }
        if (role === 'payment') {
            if (!form.instrument) {
                out.push(err('payment_form_missing_instrument', `Payment form "${form.name}" must declare an instrument ({ type: 'check', amountField }).`, form.id));
            }
            else {
                checkAmountField(form, form.instrument.amountField, out);
            }
        }
        if (role === 'coupon') {
            if (!form.appliedAmountField) {
                out.push(err('coupon_form_missing_applied_amount_field', `Coupon form "${form.name}" must declare appliedAmountField.`, form.id));
            }
            else {
                checkAmountField(form, form.appliedAmountField, out);
            }
        }
    }
    // SP3 — job-level dualControl + allocation config validation. (Pipeline
    // SHAPE checks — dualControl-requires-split etc. — live in
    // validateRemittancePipelineShape and run api-side where the relational
    // PipelineStep rows are in hand.)
    const cfg = v3.remittance;
    if (cfg && cfg.dualControl !== undefined && typeof cfg.dualControl !== 'boolean') {
        out.push(err('dual_control_not_boolean', 'remittance.dualControl must be a boolean.'));
    }
    const alloc = v3.remittance?.allocation;
    if (alloc) {
        const ids = alloc.targets.map((t) => t.id);
        const idsValid = alloc.targets.length > 0 &&
            ids.every((id) => typeof id === 'string' && id.length > 0) &&
            new Set(ids).size === ids.length &&
            alloc.targets.every((t) => typeof t.label === 'string' && t.label.length > 0);
        if (!idsValid) {
            out.push(err('allocation_target_ids_invalid', 'Allocation targets must be non-empty, with unique non-empty ids and labels.'));
        }
        const rule = alloc.rule;
        if (rule?.type === 'ratio') {
            const keys = Object.keys(rule.ratios);
            if (!keys.every((k) => ids.includes(k))) {
                out.push(err('allocation_ratio_unknown_target', 'Every ratio key must be a declared allocation target id.'));
            }
            const sum = keys.reduce((s, k) => s + rule.ratios[k], 0);
            if (keys.some((k) => !(rule.ratios[k] > 0)) || Math.abs(sum - 100) > 1e-9) {
                out.push(err('allocation_ratio_sum_invalid', 'Ratio percentages must be positive and sum to 100.'));
            }
        }
        if (rule?.type === 'priority') {
            const ok = rule.order.length === ids.length &&
                new Set(rule.order).size === rule.order.length &&
                rule.order.every((id) => ids.includes(id));
            if (!ok) {
                out.push(err('allocation_priority_order_invalid', 'Priority order must be a permutation of the allocation target ids.'));
            }
        }
    }
    // SP5 §1 — balancePolicy structural walls. NO expression parsing here (the
    // `when` clauses are parsed/scope-checked api-side in a later task, since
    // packages/shared cannot import packages/rules-engine).
    const allocTargetIds = new Set((alloc?.targets ?? []).map((t) => t.id));
    const bp = v3.remittance?.balancePolicy;
    if (bp) {
        ['shortPay', 'overPay'].forEach((sideName) => {
            const side = bp[sideName];
            if (!side)
                return;
            if (side.boundCents !== undefined && !(Number.isInteger(side.boundCents) && side.boundCents >= 1)) {
                out.push(err('remittance_policy_bound_invalid', `remittance.balancePolicy.${sideName}.boundCents must be a positive integer when present.`));
            }
            const dr = side.deltaResolution;
            const rules = dr?.rules ?? [];
            let reachableTarget = false;
            rules.forEach((rule, idx) => {
                const hasWhen = typeof rule.when === 'string' && rule.when.length > 0;
                const hasTarget = rule.targetId !== undefined;
                const hasCalc = rule.calculation !== undefined;
                if (!hasWhen || hasTarget === hasCalc) {
                    out.push(err('remittance_policy_rule_shape_invalid', `remittance.balancePolicy.${sideName}.deltaResolution.rules[${idx}] must have a non-empty "when" and exactly one of targetId|calculation.`));
                }
                if (hasTarget) {
                    reachableTarget = true;
                    if (!allocTargetIds.has(rule.targetId)) {
                        out.push(err('remittance_policy_target_unknown', `remittance.balancePolicy.${sideName}.deltaResolution.rules[${idx}].targetId "${rule.targetId}" is not a declared allocation target.`));
                    }
                }
            });
            if (dr?.defaultTargetId !== undefined) {
                reachableTarget = true;
                if (!allocTargetIds.has(dr.defaultTargetId)) {
                    out.push(err('remittance_policy_target_unknown', `remittance.balancePolicy.${sideName}.deltaResolution.defaultTargetId "${dr.defaultTargetId}" is not a declared allocation target.`));
                }
            }
            if (sideName === 'overPay' && side.mode === 'accept' && !reachableTarget) {
                out.push(err('remittance_policy_overpay_requires_target', 'remittance.balancePolicy.overPay accept requires a resolvable chain ending in a target (a rule targetId and/or defaultTargetId) — calculations alone are invalid for overPay.'));
            }
        });
    }
    // SP5 §3 — transactionFields (promote-from-attachment) structural walls.
    const BUILTIN_TXN_SCALARS = new Set([
        'ordinal', 'state', 'verdict', 'paymentTotalCents', 'appliedTotalCents', 'deltaCents',
    ]);
    const IDENTIFIER_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;
    const seenPromotionNames = new Set();
    for (const promotion of v3.remittance?.transactionFields ?? []) {
        const sourceForm = forms.find((f) => f.classificationKey === promotion.promoteFrom?.classificationKey);
        const sourceField = sourceForm?.fields?.find((f) => (f.internalName ?? f.name) === promotion.promoteFrom?.field);
        if (!sourceForm || !sourceField) {
            out.push(err('remittance_txnfield_source_unknown', `transactionFields "${promotion.name}": promoteFrom.classificationKey/field must reference an existing form and field.`));
        }
        const nameValid = typeof promotion.name === 'string' &&
            IDENTIFIER_RE.test(promotion.name) &&
            !BUILTIN_TXN_SCALARS.has(promotion.name) &&
            !seenPromotionNames.has(promotion.name);
        if (!nameValid) {
            out.push(err('remittance_txnfield_name_conflict', `transactionFields name "${promotion.name}" must be identifier-safe, unique, and not collide with a built-in transaction scalar (${[...BUILTIN_TXN_SCALARS].join('|')}).`));
        }
        seenPromotionNames.add(promotion.name);
    }
    // SP5 §2.3.1 — job-level transactionRules structural wall. NO expression
    // parsing here (see the balancePolicy note above).
    for (const rule of v3.remittance?.transactionRules ?? []) {
        const shapeValid = typeof rule.id === 'string' && rule.id.length > 0 &&
            typeof rule.name === 'string' && rule.name.length > 0 &&
            (rule.severity === 'soft' || rule.severity === 'hard') &&
            typeof rule.when === 'string' && rule.when.length > 0 &&
            typeof rule.message === 'string' && rule.message.length > 0;
        if (!shapeValid) {
            out.push(err('remittance_txnrule_shape_invalid', `remittance.transactionRules entry must have non-empty id/name/message, severity soft|hard, and non-empty when.`));
        }
    }
    return out;
}
//# sourceMappingURL=validate-config.js.map